/**
 * Authentication context providing Cognito password-based auth state and actions.
 *
 * Uses amazon-cognito-identity-js with custom in-memory storage (NFR8).
 * Tokens are NEVER stored in localStorage or sessionStorage.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js';

// ---- In-memory storage (CRITICAL: prevents localStorage usage per NFR8) ----

class MemoryStorage implements ICognitoStorage {
  private store: Record<string, string> = {};

  getItem(key: string): string | null {
    return this.store[key] ?? null;
  }

  setItem(key: string, value: string): void {
    this.store[key] = value;
  }

  removeItem(key: string): void {
    delete this.store[key];
  }

  clear(): void {
    this.store = {};
  }
}

// Singleton storage instance shared between pool and user
const memoryStorage = new MemoryStorage();

// ---- Cognito pool setup ----

function getUserPoolId(): string {
  return import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '';
}

function getClientId(): string {
  return import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
}

function createUserPool(): CognitoUserPool | null {
  // E2E test hook: allows Playwright to inject a duck-typed pool via window global.
  // This survives minification because the global name is a string literal.
  if (
    typeof window !== 'undefined' &&
    (window as unknown as Record<string, unknown>).__GBTY_TEST_USER_POOL__
  ) {
    return (window as unknown as Record<string, unknown>)
      .__GBTY_TEST_USER_POOL__ as CognitoUserPool;
  }
  const poolId = getUserPoolId();
  const clientId = getClientId();
  if (!poolId || !clientId) {
    return null;
  }
  return new CognitoUserPool({
    UserPoolId: poolId,
    ClientId: clientId,
    Storage: memoryStorage,
  });
}

// ---- Auth context types ----

/** User roles from Cognito custom:role attribute */
export type UserRole = 'admin' | 'owner' | 'driver' | 'viewer' | '';

export interface AuthUser {
  email: string;
  role: UserRole;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  /** The user's role from the JWT custom:role claim */
  userRole: UserRole;
  error: string | null;
  newPasswordRequired: boolean;
  login: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  logout: () => void;
  getIdToken: () => string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Extracts the user role from a Cognito session's ID token.
 * Reads the custom:role claim from the JWT payload.
 */
function extractRoleFromSession(session: CognitoUserSession): UserRole {
  try {
    const idToken = session.getIdToken();
    const payload = idToken.decodePayload();
    const role = payload['custom:role'] as string | undefined;
    if (
      role === 'admin' ||
      role === 'owner' ||
      role === 'driver' ||
      role === 'viewer'
    ) {
      return role;
    }
    return '';
  } catch {
    return '';
  }
}

// ---- Provider ----

interface AuthProviderProps {
  children: ReactNode;
  /** Override the user pool for testing. When provided, bypasses env var check. */
  _testUserPool?: CognitoUserPool | null;
}

export function AuthProvider({ children, _testUserPool }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);

  const cognitoUserRef = useRef<CognitoUser | null>(null);
  const sessionRef = useRef<CognitoUserSession | null>(null);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  // Store user attributes returned by newPasswordRequired callback for completeNewPassword
  const newPasswordUserAttributesRef = useRef<Record<string, string>>({});

  const userPool = useMemo(
    () => (_testUserPool !== undefined ? _testUserPool : createUserPool()),
    [_testUserPool],
  );

  const clearError = useCallback(() => setError(null), []);

  /**
   * Start token refresh interval (every 50 minutes).
   */
  const startTokenRefresh = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    refreshIntervalRef.current = setInterval(
      () => {
        const cognitoUser = cognitoUserRef.current;
        const session = sessionRef.current;
        if (!cognitoUser || !session) return;

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(refreshToken, (err, newSession) => {
          if (err) {
            // Refresh failed - session expired
            setIsAuthenticated(false);
            setUser(null);
            sessionRef.current = null;
            setError('Your session expired. Please sign in again.');
            if (refreshIntervalRef.current) {
              clearInterval(refreshIntervalRef.current);
            }
          } else {
            sessionRef.current = newSession;
          }
        });
      },
      50 * 60 * 1000, // 50 minutes
    );
  }, []);

  /**
   * Authenticate with email and password using USER_PASSWORD_AUTH flow.
   */
  const login = useCallback(
    async (email: string, password: string) => {
      if (!userPool) {
        setError('Authentication is not configured.');
        return;
      }

      setIsLoading(true);
      setError(null);
      setNewPasswordRequired(false);

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
        Storage: memoryStorage,
      });
      cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');
      cognitoUserRef.current = cognitoUser;

      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      return new Promise<void>((resolve) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session: CognitoUserSession) => {
            sessionRef.current = session;
            const role = extractRoleFromSession(session);
            setIsAuthenticated(true);
            setUser({ email, role });
            setIsLoading(false);
            startTokenRefresh();
            resolve();
          },
          onFailure: (err: Error) => {
            setIsLoading(false);
            const msg = err.message || 'Authentication failed.';
            if (
              msg.includes('User does not exist') ||
              msg.includes('UserNotFoundException')
            ) {
              setError('No account found for this email.');
            } else if (
              msg.includes('Incorrect username or password') ||
              msg.includes('NotAuthorizedException')
            ) {
              setError('Incorrect email or password.');
            } else {
              setError(msg);
            }
            resolve();
          },
          newPasswordRequired: (userAttributes: Record<string, string>) => {
            setIsLoading(false);
            // Store attributes for completeNewPasswordChallenge (remove immutable attrs)
            const {
              email_verified: _emailVerified,
              email: _email,
              ...rest
            } = userAttributes;
            newPasswordUserAttributesRef.current = rest;
            setNewPasswordRequired(true);
            resolve();
          },
        });
      });
    },
    [userPool, startTokenRefresh],
  );

  /**
   * Complete the new password challenge (first login with temporary password).
   */
  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      const cognitoUser = cognitoUserRef.current;
      if (!cognitoUser) {
        setError('No active authentication session.');
        return;
      }

      setIsLoading(true);
      setError(null);

      return new Promise<void>((resolve) => {
        cognitoUser.completeNewPasswordChallenge(
          newPassword,
          newPasswordUserAttributesRef.current,
          {
            onSuccess: (session: CognitoUserSession) => {
              sessionRef.current = session;
              const role = extractRoleFromSession(session);
              setIsAuthenticated(true);
              setUser({ email: cognitoUser.getUsername(), role });
              setNewPasswordRequired(false);
              setIsLoading(false);
              startTokenRefresh();
              resolve();
            },
            onFailure: (err: Error) => {
              setIsLoading(false);
              setError(err.message || 'Failed to set new password.');
              resolve();
            },
          },
        );
      });
    },
    [startTokenRefresh],
  );

  /**
   * Change password for an authenticated user.
   */
  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      const cognitoUser = cognitoUserRef.current;
      if (!cognitoUser) {
        throw new Error('You must be signed in to change your password.');
      }

      return new Promise<void>((resolve, reject) => {
        cognitoUser.changePassword(oldPassword, newPassword, (err) => {
          if (err) {
            reject(new Error(err.message || 'Failed to change password.'));
          } else {
            resolve();
          }
        });
      });
    },
    [],
  );

  /**
   * Sign out the user and clear all auth state.
   */
  const logout = useCallback(() => {
    const cognitoUser = cognitoUserRef.current;
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    cognitoUserRef.current = null;
    sessionRef.current = null;
    memoryStorage.clear();
    setIsAuthenticated(false);
    setUser(null);
    setNewPasswordRequired(false);
    setError(null);

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  /**
   * Get the current ID token for API calls.
   */
  const getIdToken = useCallback((): string | null => {
    const session = sessionRef.current;
    if (!session) return null;
    return session.getIdToken().getJwtToken();
  }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const userRole: UserRole = user?.role ?? '';

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      userRole,
      error,
      newPasswordRequired,
      login,
      completeNewPassword,
      changePassword,
      logout,
      getIdToken,
      clearError,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      userRole,
      error,
      newPasswordRequired,
      login,
      completeNewPassword,
      changePassword,
      logout,
      getIdToken,
      clearError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Optional auth hook that returns null when used outside AuthProvider.
 * Useful for components that may render both inside and outside auth context.
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

/**
 * Returns true if the current user has the viewer role.
 * Viewers can view admin pages but cannot perform write operations.
 */
export function useIsViewer(): boolean {
  const { userRole } = useAuth();
  return userRole === 'viewer';
}
